<template>
  <transition name="split-fade">
    <div
      v-if="show"
      class="split-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="split-title"
      @mousedown.self="$emit('cancel')"
    >
      <div class="split-panel" @mousedown.stop>
        <button type="button" class="split-close" aria-label="Close" @click="$emit('cancel')">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div class="split-header">
          <span class="split-header__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="7" width="9" height="9" rx="1" />
              <rect x="13" y="7" width="9" height="9" rx="1" />
              <path d="M12 3v3M12 18v3" />
            </svg>
          </span>
          <h3 id="split-title" class="split-header__title">
            This order ships as {{ parcels.length }} parcels
          </h3>
        </div>

        <p class="split-intro">
          {{ methodLabel }} won't book more than <strong>{{ limitLabel }}</strong> in one parcel, and
          this cart is <strong>{{ cartUnits }} bottles / {{ formatKg(cartWeightGrams) }}</strong>.
          Confirming creates <strong>{{ parcels.length }} separate orders</strong> — one label,
          invoice and tracking id each — for the same customer and payment.
        </p>

        <div v-if="discountTotal > 0" class="split-note">
          <span class="split-note__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
          <span>
            The discount is worked out on the <strong>whole {{ cartUnits }} bottles</strong>, not per
            parcel, so the customer keeps the {{ discountTierLabel || 'full tier' }} — then it's
            divided across the parcels below.
          </span>
        </div>

        <div class="split-table-wrap">
          <table class="split-table">
            <thead>
              <tr>
                <th>Parcel</th>
                <th>Items</th>
                <th class="is-num">Bottles</th>
                <th class="is-num">Weight</th>
                <th class="is-num">Value</th>
                <th class="is-num">Discount</th>
                <th class="is-num">Shipping</th>
                <th class="is-num">Payable</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(parcel, i) in parcels" :key="i">
                <td class="split-table__idx">{{ i + 1 }} of {{ parcels.length }}</td>
                <td class="split-table__items">
                  <span v-for="(line, j) in parcel.lines" :key="j" class="split-chip">
                    {{ line.name || line.sku }} <b>×{{ line.quantity }}</b>
                  </span>
                </td>
                <td class="is-num">{{ parcel.units }}</td>
                <td class="is-num">{{ formatKg(parcel.weightGrams) }}</td>
                <td class="is-num">₹{{ formatMoney(parcel.total) }}</td>
                <td class="is-num" :class="{ 'is-discount': parcel.discount > 0 }">
                  {{ parcel.discount > 0 ? '−₹' + formatMoney(parcel.discount) : '—' }}
                </td>
                <td class="is-num">
                  <span v-if="parcel.shippingPending" class="split-pending">Quoting…</span>
                  <span v-else>₹{{ formatMoney(parcel.shippingCharge + parcel.codCharge) }}</span>
                </td>
                <td class="is-num split-table__payable">₹{{ formatMoney(parcel.grandTotal) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2">Total</td>
                <td class="is-num">{{ cartUnits }}</td>
                <td class="is-num">{{ formatKg(cartWeightGrams) }}</td>
                <td class="is-num">₹{{ formatMoney(cartTotal) }}</td>
                <td class="is-num" :class="{ 'is-discount': discountTotal > 0 }">
                  {{ discountTotal > 0 ? '−₹' + formatMoney(discountTotal) : '—' }}
                </td>
                <td class="is-num">₹{{ formatMoney(shippingTotal) }}</td>
                <td class="is-num split-table__payable">₹{{ formatMoney(payableTotal) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Shipping is quoted per parcel because each is a separate booking.
             Staff quote a single-parcel figure before opening this, so saying
             nothing here would look like the charge silently went up. -->
        <p v-if="shippingDelta > 0" class="split-warn">
          Shipping is <strong>₹{{ formatMoney(shippingDelta) }} more</strong> than the single-parcel
          quote — {{ parcels.length }} parcels means {{ parcels.length }} bookings.
        </p>

        <div v-if="errorMessage" class="split-alert">{{ errorMessage }}</div>

        <div class="split-actions">
          <button type="button" class="split-btn split-btn--ghost" :disabled="isSubmitting" @click="$emit('cancel')">
            Back to the order
          </button>
          <button
            type="button"
            class="split-btn"
            :disabled="isSubmitting || isQuoting"
            @click="$emit('confirm')"
          >
            <span v-if="isSubmitting">Creating {{ parcels.length }} orders…</span>
            <span v-else-if="isQuoting">Quoting shipping…</span>
            <span v-else>Create {{ parcels.length }} orders</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// Preview of how an oversized cart will be cut into parcels, shown before
// anything is created. The cut itself comes from utils/parcelSplit.js and is
// re-done authoritatively by the server on submit — this screen only has to
// make the consequences legible, because they are not obvious: the sale
// becomes N orders, shipping is charged N times, and the discount is the one
// the WHOLE cart earned rather than what each parcel would earn alone.

export default {
  name: 'SplitParcelsModal',
  props: {
    show: { type: Boolean, default: false },
    // [{ lines, units, weightGrams, total, discount, shippingCharge,
    //    codCharge, grandTotal, shippingPending }]
    parcels: { type: Array, default: () => [] },
    methodLabel: { type: String, default: 'Speed Post' },
    limitLabel: { type: String, default: '100 bottles / 20 kg' },
    // What the same cart was quoted at as a single (un-bookable) parcel, so
    // the extra booking cost can be named rather than discovered.
    singleParcelShipping: { type: Number, default: 0 },
    discountTierLabel: { type: String, default: '' },
    isQuoting: { type: Boolean, default: false },
    isSubmitting: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' }
  },
  computed: {
    cartUnits () {
      return this.parcels.reduce((sum, p) => sum + Number(p.units || 0), 0)
    },
    cartWeightGrams () {
      return this.parcels.reduce((sum, p) => sum + Number(p.weightGrams || 0), 0)
    },
    cartTotal () {
      return this.parcels.reduce((sum, p) => sum + Number(p.total || 0), 0)
    },
    discountTotal () {
      return this.parcels.reduce((sum, p) => sum + Number(p.discount || 0), 0)
    },
    shippingTotal () {
      return this.parcels.reduce((sum, p) => sum + Number(p.shippingCharge || 0) + Number(p.codCharge || 0), 0)
    },
    payableTotal () {
      return this.parcels.reduce((sum, p) => sum + Number(p.grandTotal || 0), 0)
    },
    shippingDelta () {
      return Number((this.shippingTotal - Number(this.singleParcelShipping || 0)).toFixed(2))
    }
  },
  methods: {
    formatMoney (value) {
      return Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatKg (grams) {
      const kg = Number(grams || 0) / 1000
      return `${kg.toFixed(kg < 10 ? 2 : 1).replace(/\.?0+$/, '')} kg`
    }
  }
}
</script>

<style scoped>
.split-overlay {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(2px);
}

.split-panel {
  position: relative;
  width: 100%;
  max-width: 940px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.4rem 1.4rem 1.15rem;
  animation: split-pop 0.28s cubic-bezier(0.18, 1.2, 0.4, 1) both;
  max-height: calc(100vh - 2.5rem);
  overflow-y: auto;
}

.split-close {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.split-close:hover { background: #f3f4f6; color: #1f2a44; }
.split-close:focus { outline: none; }
.split-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.split-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.7rem;
  padding-right: 1.8rem;
}

.split-header__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ecfdf5;
  color: #0e5b46;
  flex: 0 0 auto;
}

.split-header__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0e5b46;
}

.split-intro {
  margin: 0 0 0.85rem;
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.5;
}

.split-intro strong { color: #1f2a44; font-weight: 700; }

.split-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.6rem 0.8rem;
  margin-bottom: 0.9rem;
  background: #ecfdf5;
  border: 1px solid #c7ece0;
  border-radius: 9px;
  font-size: 0.82rem;
  color: #0e5b46;
  line-height: 1.45;
}

.split-note__icon {
  flex: 0 0 auto;
  margin-top: 0.1rem;
  color: #16a07a;
  display: inline-flex;
}

.split-table-wrap {
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow-x: auto;
  margin-bottom: 0.85rem;
}

.split-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  min-width: 720px;
}

.split-table th {
  padding: 0.55rem 0.7rem;
  text-align: left;
  font-weight: 700;
  color: #4b5563;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
  white-space: nowrap;
}

.split-table td {
  padding: 0.6rem 0.7rem;
  border-bottom: 1px solid #f2f4f9;
  color: #1f2a44;
  vertical-align: top;
}

.split-table tbody tr:last-child td { border-bottom: none; }

.split-table .is-num { text-align: right; white-space: nowrap; }
.split-table .is-discount { color: #0e5b46; font-weight: 700; }

.split-table__idx {
  font-weight: 700;
  color: #0e5b46;
  white-space: nowrap;
}

.split-table__items { min-width: 210px; }

.split-table__payable { font-weight: 700; }

.split-chip {
  display: inline-block;
  padding: 0.1rem 0.45rem;
  margin: 0 0.25rem 0.25rem 0;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.74rem;
  white-space: nowrap;
}

.split-chip b { font-weight: 700; color: #1f2a44; }

.split-pending { color: #6b7280; font-style: italic; }

.split-table tfoot td {
  padding: 0.6rem 0.7rem;
  background: #fbfcfe;
  border-top: 1px solid #e5e9f2;
  font-weight: 700;
  color: #1f2a44;
}

.split-warn {
  margin: 0 0 0.85rem;
  padding: 0.55rem 0.8rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 9px;
  font-size: 0.8rem;
  color: #92400e;
  line-height: 1.45;
}

.split-alert {
  padding: 0.6rem 0.85rem;
  margin-bottom: 0.85rem;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
}

.split-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.split-btn {
  padding: 0.55rem 1.15rem;
  border-radius: 9px;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  font-family: inherit;
}

.split-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.3);
  filter: brightness(1.04);
}

.split-btn:active:not(:disabled) { transform: translateY(0); filter: brightness(0.96); }
.split-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.split-btn:focus { outline: none; }
.split-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22), 0 0 0 3px rgba(22, 160, 122, 0.3);
}

.split-btn--ghost {
  background: #ffffff;
  color: #4b5563;
  border: 1px solid #e5e9f2;
  box-shadow: none;
}

.split-btn--ghost:hover:not(:disabled) {
  background: #f9fafb;
  color: #1f2a44;
  box-shadow: none;
}

.split-btn--ghost:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.24);
}

.split-fade-enter-active,
.split-fade-leave-active { transition: opacity 0.18s ease; }
.split-fade-enter,
.split-fade-leave-to { opacity: 0; }

@keyframes split-pop {
  0%   { opacity: 0; transform: translateY(8px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 575px) {
  .split-panel { padding: 1.1rem 1rem 0.95rem; }
  .split-actions .split-btn { flex: 1 1 auto; }
}
</style>
